import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Container,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../../services/api';
import Messages from './Messages';
import Templates from './Templates';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-tabpanel-${index}`}
      aria-labelledby={`whatsapp-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `whatsapp-tab-${index}`,
    'aria-controls': `whatsapp-tabpanel-${index}`,
  };
}

const WhatsApp = () => {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [integrationData, setIntegrationData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Remove tab parameter from URL when manually switching tabs
    if (searchParams.get('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams);
    }
  };

  // Define all tabs
  const tabs = [
    { label: 'Messages', component: <Messages /> },
    { label: 'Templates', component: <Templates /> },
  ];

  useEffect(() => {
    checkWhatsAppConnection();
  }, []);

  useEffect(() => {
    // Check if there's a tab parameter in URL
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = tabs.findIndex(tab => 
        tab.label.toLowerCase().replace(/\s+/g, '-') === tabParam.toLowerCase()
      );
      if (tabIndex !== -1) {
        setValue(tabIndex);
      }
      // Clean up URL parameter
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const checkWhatsAppConnection = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/integrations/');
      const data = Array.isArray(response.data) ? response.data : [];
      const whatsappIntegration = data.find(integration => integration.id === 'whatsapp');
      
      if (whatsappIntegration && whatsappIntegration.connected) {
        setWhatsappConnected(true);
        setIntegrationData(whatsappIntegration);
      } else {
        setWhatsappConnected(false);
      }
    } catch (error) {
      console.error('Error checking WhatsApp connection:', error);
      setWhatsappConnected(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!whatsappConnected) {
    return (
      <Container maxWidth={false} disableGutters>
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            WhatsApp is not connected. Please connect your WhatsApp account in Settings → Integrations.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      disableGutters
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        px: 2, 
        py: 1.5, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Typography variant="body1" color="text.secondary">
          Manage your WhatsApp messages, templates, and automated triggers
        </Typography>
        {integrationData?.connectedPage && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Connected Phone: {integrationData.connectedPage.name}
            {integrationData.connectedPage.phone_number && ` (${integrationData.connectedPage.phone_number})`}
          </Typography>
        )}
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        overflow: 'auto',
        px: 2,
        pt: 1
      }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="whatsapp tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {tabs.map((tab, index) => (
            <TabPanel key={index} value={value} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default WhatsApp;
